import { translateApiError, useTranslation } from '@jq4race/i18n';
import type {
  ApplyCompResponse,
  RegisterCashPaymentResponse,
  RetrievalQueueItem,
} from '@jq4race/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@jq4race/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Car, X } from 'lucide-react';
import { useState } from 'react';
import { showStatusToast } from '../../../../app/status-toast-store';
import { useCompsConfig } from '../../../location-context/hooks/use-comps-config';
import { useApplyComp } from '../../../pickup-queue/hooks/use-apply-comp';
import { useClaimTicket } from '../../../pickup-queue/hooks/use-claim-ticket';
import { useDeliverTicket } from '../../../pickup-queue/hooks/use-deliver-ticket';
import { useMarkReady } from '../../../pickup-queue/hooks/use-mark-ready';
import { useRegisterCashPayment } from '../../../pickup-queue/hooks/use-register-cash-payment';
import {
  MY_ACTIVE_KEY,
  RETRIEVAL_QUEUE_KEY,
  useMyActiveTickets,
  useRetrievalQueue,
} from '../../../pickup-queue/hooks/use-retrieval-queue';
import { CashPaymentDialog } from '../../../pickup-queue/ui/components/CashPaymentDialog';
import { CompDialog } from '../../../pickup-queue/ui/components/CompDialog';
import { MyActiveTickets } from '../../../pickup-queue/ui/components/MyActiveTickets';
import { QueueList } from '../../../pickup-queue/ui/components/QueueList';
import { TicketPhotosPanel } from '../../../pickup-queue/ui/components/TicketPhotosPanel';
import { EmptyState } from '../../../shell/ui/EmptyState';
import { PageHeader } from '../../../shell/ui/PageHeader';

export function HomePage() {
  const { t } = useTranslation('shift');
  const qc = useQueryClient();
  const queue = useRetrievalQueue();
  const myActive = useMyActiveTickets();
  const claimMutation = useClaimTicket();
  const deliverMutation = useDeliverTicket();
  const markReadyMutation = useMarkReady();
  const cashMutation = useRegisterCashPayment();
  const compMutation = useApplyComp();

  // JQ4-19 slice 3: comp config for conditional button visibility (Scenario 5)
  const { comps } = useCompsConfig();

  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);

  // JQ4-26: deliver flow state machine
  const [confirmingDeliverId, setConfirmingDeliverId] = useState<string | null>(null);
  const [deliveringId, setDeliveringId] = useState<string | null>(null);

  // post-payment-delay-recharge S1: mark-ready flow state (no confirm dialog — idempotent action)
  const [markingReadyId, setMarkingReadyId] = useState<string | null>(null);

  // JQ4-38: cash payment flow state machine
  const [cashTicketId, setCashTicketId] = useState<string | null>(null);
  const [registeringCashId, setRegisteringCashId] = useState<string | null>(null);
  const [cashErrorMessage, setCashErrorMessage] = useState<string | null>(null);

  // JQ4-19: comp flow state machine
  const [compTicketId, setCompTicketId] = useState<string | null>(null);
  const [applyingCompId, setApplyingCompId] = useState<string | null>(null);
  const [compErrorMessage, setCompErrorMessage] = useState<string | null>(null);

  const queueItems = queue.data ?? [];
  const activeItems = myActive.data ?? [];
  const isEmpty = queueItems.length === 0 && activeItems.length === 0;

  function handleClaim(id: string) {
    setClaimingId(id);
    setConflictMessage(null);

    claimMutation.mutate(id, {
      onSuccess: () => {
        setClaimingId(null);
      },
      onError: (err) => {
        setClaimingId(null);
        const code = err instanceof Error && 'code' in err ? (err as { code: string }).code : null;
        if (code === 'ticket_already_claimed') {
          setConflictMessage(t('messages.alreadyClaimed'));
          // Refetch the queue so the claimed ticket disappears
          void qc.invalidateQueries({ queryKey: RETRIEVAL_QUEUE_KEY });
        }
      },
    });
  }

  // JQ4-26: open the confirm dialog
  function handleDeliver(id: string) {
    setConfirmingDeliverId(id);
    setConflictMessage(null);
  }

  // JQ4-26: confirmed delivery — fire the mutation
  function handleDeliverConfirm() {
    if (!confirmingDeliverId) return;
    const id = confirmingDeliverId;
    setConfirmingDeliverId(null);
    setDeliveringId(id);

    deliverMutation.mutate(id, {
      onSuccess: () => {
        setDeliveringId(null);
      },
      onError: (err) => {
        setDeliveringId(null);
        const code = err instanceof Error && 'code' in err ? (err as { code: string }).code : null;
        if (code === 'ticket_already_delivered') {
          setConflictMessage(t('messages.alreadyDelivered'));
          void qc.invalidateQueries({ queryKey: MY_ACTIVE_KEY });
        } else if (code === 'payment_required') {
          // JQ4-39 S2 (REQ-UI-04): payment gate — no optimistic update (REQ-UI-05)
          setConflictMessage(t('messages.paymentRequired'));
        }
      },
    });
  }

  // post-payment-delay-recharge S1: fire the mark-ready mutation directly (idempotent — no confirm dialog)
  function handleMarkReady(id: string) {
    setMarkingReadyId(id);
    setConflictMessage(null);

    markReadyMutation.mutate(id, {
      onSuccess: (response) => {
        setMarkingReadyId(null);
        // QA R2-03: the row now shows the "Ready" chip; confirm it with a toast too.
        showStatusToast(
          t('pickup-queue:toast.markedReady', { ticketNumber: response.ticketNumber }),
        );
      },
      onError: (err) => {
        setMarkingReadyId(null);
        const code = err instanceof Error && 'code' in err ? (err as { code: string }).code : null;
        if (code === 'ticket_already_delivered') {
          setConflictMessage(t('messages.alreadyDeliveredOrCancelled'));
          void qc.invalidateQueries({ queryKey: MY_ACTIVE_KEY });
        } else {
          // QA R2-03: a failure used to be swallowed — the valet saw nothing happen.
          setConflictMessage(t('messages.markReadyFailed'));
        }
      },
    });
  }

  // Derive the plate + ticket number for the confirm dialog description
  const confirmingItem = activeItems.find((item) => item.id === confirmingDeliverId);

  // JQ4-38: derive item for the cash payment dialog description
  const cashTicketItem = activeItems.find((item) => item.id === cashTicketId);

  // JQ4-38: open the cash payment dialog for a ticket
  function handleRegisterCash(id: string) {
    setCashTicketId(id);
    setCashErrorMessage(null);
  }

  // JQ4-38: confirmed cash payment — fire the mutation
  function handleCashConfirm(amountCents: number) {
    if (!cashTicketId) return;
    const id = cashTicketId;
    setRegisteringCashId(id);
    setCashErrorMessage(null);

    cashMutation.mutate(
      { ticketId: id, amountCents },
      {
        onSuccess: (response: RegisterCashPaymentResponse) => {
          // Optimistic update: patch the ticket in the local list without a GET refetch (REQ-7, S7.4)
          qc.setQueryData<RetrievalQueueItem[]>(MY_ACTIVE_KEY, (prev) =>
            prev?.map((item) =>
              item.id === id
                ? {
                    ...item,
                    paymentStatus: response.paymentStatus,
                  }
                : item,
            ),
          );
          setRegisteringCashId(null);
          setCashTicketId(null);
          // QA R2-15: the dialog used to close silently — confirm what was recorded.
          showStatusToast(
            t('pickup-queue:toast.cashRecorded', { ticketNumber: response.ticketNumber }),
          );
        },
        onError: (err) => {
          setRegisteringCashId(null);
          const message = translateApiError(err, t, { fallback: t('common:errors.unexpected') });
          setCashErrorMessage(message);
          // Dialog stays open — cashTicketId is still set
        },
      },
    );
  }

  // JQ4-38: cancel / close the cash payment dialog
  function handleCashCancel() {
    setCashTicketId(null);
    setCashErrorMessage(null);
    setRegisteringCashId(null);
  }

  // JQ4-19: derive item for the comp dialog description
  const compTicketItem = activeItems.find((item) => item.id === compTicketId);

  // JQ4-19: open the comp dialog for a ticket
  function handleApplyComp(id: string) {
    setCompTicketId(id);
    setCompErrorMessage(null);
  }

  // JQ4-19: confirmed comp — fire the mutation
  function handleCompConfirm(reason: string) {
    if (!compTicketId) return;
    const id = compTicketId;
    setApplyingCompId(id);
    setCompErrorMessage(null);

    compMutation.mutate(
      { ticketId: id, reason },
      {
        onSuccess: (response: ApplyCompResponse) => {
          // Optimistic update: patch the ticket in the local list without a GET refetch
          qc.setQueryData<RetrievalQueueItem[]>(MY_ACTIVE_KEY, (prev) =>
            prev?.map((item) =>
              item.id === id ? { ...item, paymentStatus: response.paymentStatus } : item,
            ),
          );
          setApplyingCompId(null);
          setCompTicketId(null);
          showStatusToast(
            t('pickup-queue:toast.compRecorded', { ticketNumber: compTicketItem?.ticketNumber }),
          );
        },
        onError: (err) => {
          setApplyingCompId(null);
          const message = translateApiError(err, t, { fallback: t('common:errors.unexpected') });
          setCompErrorMessage(message);
          // Dialog stays open — compTicketId is still set
        },
      },
    );
  }

  // JQ4-19: cancel / close the comp dialog
  function handleCompCancel() {
    setCompTicketId(null);
    setCompErrorMessage(null);
    setApplyingCompId(null);
  }

  return (
    <>
      <PageHeader eyebrow={t('eyebrow')} title={t('title')} description={t('description')} />

      {conflictMessage ? (
        <div
          role="alert"
          className="mb-4 flex items-start justify-between gap-3 rounded-sm border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning"
        >
          <span>{conflictMessage}</span>
          <button
            type="button"
            aria-label={t('dismissAria')}
            onClick={() => setConflictMessage(null)}
            className="shrink-0"
          >
            <X className="size-4" />
          </button>
        </div>
      ) : null}

      {isEmpty ? (
        <EmptyState icon={Car} title={t('emptyTitle')} description={t('emptyDescription')} />
      ) : (
        <div className="flex flex-col gap-6">
          {queueItems.length > 0 ? (
            <section>
              <h2 className="jq-h5 mb-3 text-foreground">{t('queueHeading')}</h2>
              <QueueList items={queueItems} onClaim={handleClaim} claimingId={claimingId} />
            </section>
          ) : null}

          {activeItems.length > 0 ? (
            <section>
              <h2 className="jq-h5 mb-3 text-foreground">{t('myActiveHeading')}</h2>
              <MyActiveTickets
                items={activeItems}
                onDeliver={handleDeliver}
                deliveringId={deliveringId}
                onMarkReady={handleMarkReady}
                markingReadyId={markingReadyId}
                onRegisterCash={handleRegisterCash}
                registeringCashId={registeringCashId}
                onApplyComp={comps.authorizedForActor ? handleApplyComp : undefined}
                applyingCompId={applyingCompId}
                compsAuthorized={comps.authorizedForActor}
              />
            </section>
          ) : null}
        </div>
      )}

      {/* JQ4-26: delivery confirm dialog */}
      <Dialog
        open={confirmingDeliverId !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmingDeliverId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('deliverDialog.title')}</DialogTitle>
            <DialogDescription>
              {confirmingItem
                ? `${confirmingItem.plate} · #${confirmingItem.ticketNumber}`
                : t('deliverDialog.descriptionFallback')}
            </DialogDescription>
          </DialogHeader>

          {/* ticket-photos-for-valet: validate the vehicle's condition at the
              moment that matters — delivery. Purely additive to this dialog:
              a slow/failed/empty photo fetch never disables Confirm/Cancel
              below (see TicketPhotosPanel's own non-blocking states). */}
          <TicketPhotosPanel ticketId={confirmingDeliverId} plate={confirmingItem?.plate ?? ''} />

          <DialogFooter>
            <button
              type="button"
              onClick={() => setConfirmingDeliverId(null)}
              className="rounded-sm px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
            >
              {t('common:actions.cancel')}
            </button>
            <button
              type="button"
              aria-label={t('deliverDialog.confirmAria')}
              onClick={handleDeliverConfirm}
              className="rounded-sm bg-success px-4 py-2 text-sm font-medium text-success-foreground"
            >
              {t('common:actions.confirm')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* JQ4-38: cash payment dialog */}
      {cashTicketItem ? (
        <CashPaymentDialog
          open={cashTicketId !== null}
          ticketId={cashTicketItem.id}
          ticketNumber={cashTicketItem.ticketNumber}
          plate={cashTicketItem.plate}
          onConfirm={handleCashConfirm}
          onCancel={handleCashCancel}
          errorMessage={cashErrorMessage}
          submitting={registeringCashId !== null}
        />
      ) : null}

      {/* JQ4-19: comp (cortesía) dialog */}
      {compTicketItem ? (
        <CompDialog
          open={compTicketId !== null}
          ticketNumber={compTicketItem.ticketNumber}
          plate={compTicketItem.plate}
          onConfirm={handleCompConfirm}
          onCancel={handleCompCancel}
          errorMessage={compErrorMessage}
          submitting={applyingCompId !== null}
        />
      ) : null}
    </>
  );
}
